import { CoreModalDialog, CoreModalDialogFooter, MMButton } from '@components'
import BlockedIcon from '@images/icons/error.svg?react'
import SuccessIcon from '@images/icons/success-check.svg?react'
import cn from 'classnames'
import { useCreateCallRequestMutation } from 'mmfintech-backend-api'
import type { CallRequestReason, ErrorObject } from 'mmfintech-backend-api/types'
import { tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useEffect, useState } from 'react'
import { getCallRequestReasonOptions, MAX_DETAILS_LENGTH } from '../../utils'
import './CallRequestModal.scss'

type Phase = 'form' | 'confirmation' | 'pending' | 'blocked'

interface Props {
  visible: boolean
  mode: 'form' | 'pending'
  onClose: () => void
}

function ClockGlyph() {
  return (
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='12' cy='12' r='9' />
      <path d='M12 7v5l3 2' />
    </svg>
  )
}

function CallRequestModal({ visible, mode, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>(mode)
  const [reason, setReason] = useState<CallRequestReason | null>(null)
  const [details, setDetails] = useState('')

  const [createCallRequest, { isLoading: submitting, error: submitError }] = useCreateCallRequestMutation()

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the flow whenever the modal is (re)opened
  useEffect(() => {
    if (visible) {
      setPhase(mode)
      setReason(null)
      setDetails('')
    }
  }, [visible])

  const reasonOptions = getCallRequestReasonOptions()
  const canSubmit = reason !== null

  // PLAT-16226: FRONTEND.COMMON.ERROR.VALIDATION_FAILED is deliberately suppressed by ErrorDisplay's
  // prepareErrorMessage (it expects field-level cause messages to be surfaced by the caller,
  // not the generic banner) — pull the first cause message out ourselves, same pattern as
  // EditCardPinModal.tsx.
  const submitErrorMessage =
    (submitError as ErrorObject)?.cause?.[0]?.cause || (submitError as ErrorObject)?.error || null

  async function handleSubmit() {
    if (!canSubmit) return
    try {
      const response = await createCallRequest({
        reason,
        details: details.trim() ? details.trim() : undefined
      }).unwrap()

      if (response.messageType === 'DNC_BLOCKED') {
        setPhase('blocked')
      } else if (response.messageType === 'ALREADY_PENDING') {
        setPhase('pending')
      } else {
        setPhase('confirmation')
      }
    } catch {
      // error is already reflected reactively via submitErrorMessage and rendered inline below
    }
  }

  const formContent = (
    <div className='crm-form'>
      <p className='crm-subtitle'>
        {tr(
          'FRONTEND.CALL_REQUEST.FORM.SUBTITLE',
          "A sales agent will call you back. Tell us what it's about so we can prepare."
        )}
      </p>

      <span className='crm-field-label'>{tr('FRONTEND.CALL_REQUEST.FORM.REASON_LABEL', 'Reason for the call')}</span>
      <div className='crm-reason-list'>
        {reasonOptions.map(option => (
          <div
            key={option.value}
            className={cn('crm-reason-item', { selected: option.value === reason })}
            onClick={() => {
              setReason(option.value)
              if (option.value !== 'OTHER') setDetails('')
            }}
          >
            <span>{option.label}</span>
            <span className='crm-reason-item-check' />
          </div>
        ))}
      </div>

      {reason === 'OTHER' && (
        <div className='crm-details-wrap'>
          <textarea
            className='crm-details-textarea'
            maxLength={MAX_DETAILS_LENGTH}
            value={details}
            onChange={e => setDetails(e.target.value)}
            placeholder={tr(
              'FRONTEND.CALL_REQUEST.FORM.DETAILS_PLACEHOLDER',
              'Please tell us more about your request…'
            )}
          />
          <span className={cn('crm-char-count', { warn: details.length > MAX_DETAILS_LENGTH * 0.9 })}>
            {details.length}/{MAX_DETAILS_LENGTH}
          </span>
        </div>
      )}

      <ErrorDisplay error={submitErrorMessage} />

      <CoreModalDialogFooter className='crm-footer'>
        <div className='crm-footer-actions'>
          <MMButton
            type='button'
            variant='alternative'
            size='large'
            caption={tr('FRONTEND.CALL_REQUEST.FORM.SUBMIT', 'Request a call')}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!canSubmit || submitting}
          />
          <MMButton
            type='button'
            variant='tertiary'
            size='large'
            caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            onClick={onClose}
          />
        </div>
      </CoreModalDialogFooter>
    </div>
  )

  const confirmationContent = (
    <div className='crm-result'>
      <div className='crm-icon crm-icon--success'>
        <SuccessIcon />
      </div>
      <p className='crm-result-title'>
        {tr('FRONTEND.CALL_REQUEST.CONFIRMATION.TITLE', 'Your call request has been received')}
      </p>
      <p className='crm-result-body'>
        {tr(
          'FRONTEND.CALL_REQUEST.CONFIRMATION.BODY',
          'A sales agent will call you back shortly on the phone number on file.'
        )}
      </p>
      <MMButton
        type='button'
        variant='alternative'
        size='large'
        fullWidth
        caption={tr('FRONTEND.BUTTONS.OK', 'OK')}
        onClick={onClose}
      />
    </div>
  )

  const pendingContent = (
    <div className='crm-result'>
      <div className='crm-icon crm-icon--pending'>
        <ClockGlyph />
      </div>
      <p className='crm-result-title'>{tr('FRONTEND.CALL_REQUEST.PENDING.TITLE', 'Call already requested')}</p>
      <p className='crm-result-body'>
        {tr('FRONTEND.CALL_REQUEST.PENDING.BODY', 'We will contact you shortly on the phone number on file.')}
      </p>
      <MMButton
        type='button'
        variant='alternative'
        size='large'
        fullWidth
        caption={tr('FRONTEND.BUTTONS.OK', 'OK')}
        onClick={onClose}
      />
    </div>
  )

  const blockedContent = (
    <div className='crm-result'>
      <div className='crm-icon crm-icon--blocked'>
        <BlockedIcon />
      </div>
      <p className='crm-result-title'>{tr('FRONTEND.CALL_REQUEST.BLOCKED.TITLE', "We're unable to place this call")}</p>
      <p className='crm-result-body'>
        {tr(
          'FRONTEND.CALL_REQUEST.BLOCKED.BODY',
          "You're not currently eligible to receive a call from us. Please contact support for more information."
        )}
      </p>
      <MMButton
        type='button'
        variant='alternative'
        size='large'
        fullWidth
        caption={tr('FRONTEND.BUTTONS.OK', 'OK')}
        onClick={onClose}
      />
    </div>
  )

  const contentByPhase: Record<Phase, React.ReactNode> = {
    form: formContent,
    confirmation: confirmationContent,
    pending: pendingContent,
    blocked: blockedContent
  }

  return (
    <CoreModalDialog
      visible={visible}
      onClose={onClose}
      options={{ size: phase === 'form' ? undefined : 'auto', closeOnClickOutside: true }}
      header={phase === 'form' ? tr('FRONTEND.CALL_REQUEST.FORM.TITLE', 'Request a call') : undefined}
      content={contentByPhase[phase]}
    />
  )
}

export default CallRequestModal
